import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@rocco/utils/cn';
import type { LabelledImageHiddenResult } from '~/modules-rocco/projects/api/set-labelled-image-hidden.server';
import { LABELLED_IMAGE_VISIBILITY_API_ROUTE } from '~/routes/remix-api.labelled-image-visibility';

interface ImageVisibilityButtonProps {
    /** The LibraryImage on screen; the hide covers every copy of the same file. */
    libraryImageID: number;
    /** Whether the photograph is currently hidden from visitors. */
    hidden: boolean;
    /** Called once the backend has saved the change, with the new hidden state. */
    onChanged: (libraryImageID: number, hidden: boolean) => void;
}

/**
 * The walls read the search index, which the backend refreshes on a schedule after the change is
 * saved. Until it has, the wall keeps serving the previous state, so a staff member who reloads
 * straight away still sees the photograph as it was. The toast says so.
 */
const INDEX_DELAY_NOTICE = 'The walls update within a few minutes.';

const SAVE_FAILED_NOTICE = 'The change could not be saved.';

/**
 * Asks the backend to hide or restore a photograph. Resolves to the backend's answer, and to a
 * failure for anything that stops one arriving (network error, non-JSON reply).
 */
const requestVisibility = async (
    libraryImageID: number,
    hidden: boolean,
): Promise<LabelledImageHiddenResult> => {
    try {
        const response = await fetch(LABELLED_IMAGE_VISIBILITY_API_ROUTE, {
            method: 'POST',
            body: new URLSearchParams({
                libraryImageID: String(libraryImageID),
                hidden: String(hidden),
            }),
        });
        return await response.json();
    } catch {
        return { ok: false, message: SAVE_FAILED_NOTICE, changed: 0 };
    }
};

/**
 * Staff control in the Spaces / Styles viewer: hides the photograph on screen from the walls, or
 * puts a hidden one back. The wall only offers it to staff, and the backend refuses anyone else.
 *
 * The request is not tied to this component's lifetime: swiping to another slide remounts the
 * button, and the answer still reports through `onChanged` and the toast.
 */
export const ImageVisibilityButton = ({
    libraryImageID,
    hidden,
    onChanged,
}: ImageVisibilityButtonProps) => {
    const [saving, setSaving] = useState(false);

    const toggle = async () => {
        const nowHidden = !hidden;
        setSaving(true);
        const answer = await requestVisibility(libraryImageID, nowHidden);
        setSaving(false);
        if (!answer.ok) {
            toast.error(answer.message || SAVE_FAILED_NOTICE);
            return;
        }
        // Saved, but nothing needed changing: the marker on screen came from the index, not from a
        // staff decision. Say what the backend found and leave the state alone.
        if (answer.changed === 0) {
            toast.info(answer.message);
            return;
        }
        onChanged(libraryImageID, nowHidden);
        toast.success(nowHidden ? 'Image hidden' : 'Image restored', {
            description: INDEX_DELAY_NOTICE,
        });
    };

    const Icon = hidden ? Eye : EyeOff;

    return (
        <button
            type="button"
            onClick={toggle}
            disabled={saving}
            className={cn(
                'inline-flex h-[3rem] cursor-pointer items-center gap-[0.375rem] rounded-[1.375rem] px-[1.25rem]',
                'bg-white text-[1rem] font-medium text-[#181818] transition-colors hover:bg-white/85',
                'disabled:cursor-wait disabled:opacity-60',
            )}
        >
            <Icon aria-hidden="true" className="size-4" />
            {hidden ? 'Restore' : 'Hide'}
        </button>
    );
};
